import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Phone } from "lucide-react";
import { NAV_LINKS, PHONE, PHONE_TEL } from "@/data/content";

const Wordmark = () => (
  <Link to="/" className="group flex items-center gap-3" data-testid="nav-logo">
    <span className="relative flex h-10 w-10 items-center justify-center rounded-md border border-[#2B9FD7]/40 bg-[#10232F]">
      <span className="absolute h-2 w-2 rounded-full bg-[#FF8A00]" />
      <span className="hotspot-ring absolute h-5 w-5 rounded-full border border-[#FF8A00]/60" />
    </span>
    <span className="flex flex-col">
      <span className="font-heading text-sm font-bold uppercase tracking-[0.14em] text-[#F5F7F9] sm:text-base">
        Brandpreventie <span className="text-[#62C3F0]">Recycling</span>
      </span>
      <span className="text-[10px] font-medium tracking-wide text-[#CBD3D9]/70">
        Een oplossing van Beveilig Alles B.V.
      </span>
    </span>
  </Link>
);

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [pathname]);

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-[background-color,border-color,backdrop-filter] duration-500 ${
        scrolled || open
          ? "border-b border-[#2B9FD7]/15 bg-[#090D10]/85 backdrop-blur-xl"
          : "border-b border-transparent bg-transparent"
      }`}
      data-testid="main-navbar"
    >
      <div className="mx-auto flex h-[72px] max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-10">
        <Wordmark />
        <nav className="hidden items-center gap-7 xl:flex" aria-label="Hoofdnavigatie">
          {NAV_LINKS.map((link) => (
            <Link
              key={link.label}
              to={link.to}
              data-testid={`nav-link-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
              className="whitespace-nowrap text-[13px] font-semibold tracking-wide text-[#CBD3D9] transition-colors duration-300 hover:text-[#62C3F0]"
            >
              {link.label}
            </Link>
          ))}
          <Link
            to="/offerte"
            data-testid="nav-cta-locatieanalyse"
            className="whitespace-nowrap rounded-full bg-[#2B9FD7] px-5 py-2.5 font-heading text-[13px] font-semibold text-[#090D10] transition-[background-color,box-shadow,transform] duration-300 hover:-translate-y-0.5 hover:bg-[#62C3F0] hover:shadow-[0_0_24px_rgba(98,195,240,0.45)]"
          >
            Gratis locatieanalyse
          </Link>
        </nav>
        <button
          type="button"
          className="flex h-11 w-11 items-center justify-center rounded-full border border-white/15 text-[#F5F7F9] xl:hidden"
          onClick={() => setOpen(!open)}
          aria-label={open ? "Menu sluiten" : "Menu openen"}
          data-testid="nav-mobile-toggle"
        >
          {open ? <X size={20} /> : <Menu size={20} />}
        </button>
      </div>
      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.35, ease: "easeOut" }}
            className="overflow-hidden border-t border-white/5 bg-[#090D10]/95 backdrop-blur-xl xl:hidden"
            aria-label="Mobiele navigatie"
            data-testid="nav-mobile-menu"
          >
            <div className="flex flex-col gap-1 px-6 py-6">
              {NAV_LINKS.map((link) => (
                <Link
                  key={link.label}
                  to={link.to}
                  className="rounded-lg px-3 py-3 font-heading text-base font-semibold text-[#F5F7F9] transition-colors hover:bg-white/5"
                >
                  {link.label}
                </Link>
              ))}
              <Link
                to="/offerte"
                className="mt-3 rounded-full bg-[#2B9FD7] px-5 py-3.5 text-center font-heading text-sm font-semibold text-[#090D10]"
              >
                Gratis locatieanalyse
              </Link>
              <a
                href={PHONE_TEL}
                className="mt-2 flex items-center justify-center gap-2 rounded-full border border-white/15 px-5 py-3.5 font-heading text-sm font-semibold text-[#F5F7F9]"
              >
                <Phone size={16} /> Bel {PHONE}
              </a>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};
